import { Text } from '@mantine/core';
import { match } from 'ts-pattern';
import { IconBug, IconGolfFilled, IconSparkles } from '@tabler/icons-react';
import type { AIMessage } from '~/common/vm/protocol';
import { CollapsibleCard } from '../CollapsibleCard';
import { api } from '~/trpc-extra/api';

export function AutohealStepStatusCard({ message }: { message: Extract<AIMessage, { type: 'autoheal-step-status' }> }) {
  const { mutateAsync: getLangSmithAiTraceUrl } = api.admin.getLangSmithAiTraceUrl.useMutation();

  const config = match(message.status)
    .with('begin', () => ({
      icon: IconSparkles,
      iconClassname: 'text-gray-500 dark:text-gray-400',
      title: 'Auto-heal Started',
    }))
    .with('finished', () => ({
      icon: IconGolfFilled,
      iconClassname: 'text-gray-500 dark:text-gray-400',
      title: 'Auto-heal Finished',
    }))
    .exhaustive();

  return (
    <>
      {message.status === 'begin' && (
        <Text
          size="xs"
          className="mb-2 text-center text-gray-400 dark:text-gray-500"
        >
          Auto-heal Started at {new Date().toLocaleTimeString()}
        </Text>
      )}
      <CollapsibleCard
        icon={config.icon}
        title={config.title}
        defaultExpanded={true}
        classNames={{
          icon: config.iconClassname,
        }}
        headerButtons={[
          {
            name: 'Debug Autoheal in LangSmith',
            icon: IconBug,
            adminOnly: true,
            callback: async () => {
              try {
                const traceUrl = await getLangSmithAiTraceUrl({
                  executionId: message.executionId,
                  stepId: message.stepId,
                  initiator: message.initiator,
                });
                window.open(traceUrl, '_blank');
              } catch (error) {
                console.error('Failed to get LangSmith autoheal trace URL:', error);
              }
            },
          },
        ]}
      >
        <div className="space-y-2">
          <div className="rounded border border-gray-200 bg-gray-50 px-3 dark:border-gray-700 dark:bg-gray-800">
            <Text
              size="xs"
              className="italic leading-relaxed"
            >
              &quot;{message.prompt}&quot;
            </Text>
          </div>
        </div>
      </CollapsibleCard>
      {message.status === 'finished' && (
        <Text
          size="xs"
          className="mt-2 text-center text-gray-400 dark:text-gray-500"
        >
          Auto-heal Finished at {new Date().toLocaleTimeString()}
        </Text>
      )}
    </>
  );
}
